import { PlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { SubscriptionTabDefinition } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectAccountDetails } from '@mm/state/selectors/subscriptionSelectors';
import { abbreviateNumber } from '@mm/utils';
import { isEmpty, isNil, isNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import { PrimaryToolTip } from '../../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { useBillingDetails } from '../use-billing-details';

export const LevelChoosesTemplate = ({
  page,
  loadPricesForNextLevel,
  planTab,
  planInfoMemoised,
}: {
  page: EnhancedBillingPage;
  loadPricesForNextLevel: (subscriptionId: number, level: number) => void;
  planTab: SubscriptionTabDefinition;
  planInfoMemoised: Partial<PlanInfo>;
}) => {
  const {
    currentSubscription,
    plansAllotmentsOptions,
    subscriptionEndsAt,
    billingPeriod,
    allotmentLevelDowngrade,
  } = useBillingDetails(page, planInfoMemoised, planTab.teamSubscription);
  const accountDetails = useSelector(selectAccountDetails);
  const [selectedLevel, setSelectedLevel] = useState(plansAllotmentsOptions[0]);
  const [selectedLevelChanged, setSelectedLevelChanged] = useState(false);
  const subscription = currentSubscription || page.subscription;

  const { openBillingModal, openGeneralTextModal } = useModalActions();

  const {
    isProUnicorn,
    isProPlan,
    isAnyTeamPlan,
    isFreePlan,
    isInstachamp,
    isCustomTeam,
    isLegacyTeamPlan,
  } = planInfoMemoised;

  useEffect(() => {
    if (subscription) {
      const { sendAllotmentLevel } = subscription;

      const planAllotmentCurrent = plansAllotmentsOptions.find(
        allotment => Number(allotment.level) === sendAllotmentLevel,
      );
      if (sendAllotmentLevel && planAllotmentCurrent) {
        setSelectedLevel(planAllotmentCurrent);
      } else {
        setSelectedLevel(plansAllotmentsOptions[0]);
      }
    }
  }, [planInfoMemoised, JSON.stringify(plansAllotmentsOptions)]);

  const isOneOfSupportedPlans =
    isProUnicorn || isProPlan || isAnyTeamPlan || isInstachamp;
  const showPopup = isNumber(allotmentLevelDowngrade);

  const loweredSendsAmount = isNumber(allotmentLevelDowngrade)
    ? plansAllotmentsOptions.find(
        option => option.level === allotmentLevelDowngrade,
      )?.name
    : 0;

  const onDropdownItemChange = (plan: {
    id: number;
    name: string;
    level: number;
  }) => {
    setSelectedLevel(plan);
    setSelectedLevelChanged(true);
  };

  const getFormatedPriceAndName = (option: DropdownOption) => {
    const { subscription } = page || {};
    const { level, name } = option;
    const price = level * (subscription?.sendAllotmentAddOnPrice || 0);
    const formattedName = name?.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    return { price, formattedName };
  };

  const onOpenConfirmSendsModal = () => {
    if (!subscription) {
      return;
    }
    const { price, formattedName } = getFormatedPriceAndName(selectedLevel);

    loadPricesForNextLevel(subscription.id, selectedLevel.level);

    const { sendAllotmentLevel } = subscription;

    const billingPeriod = currentSubscription?.billingPeriodLength;
    const customPeriodLength = currentSubscription?.planIntervalLength;

    const period =
      billingPeriod === 'annually'
        ? 'yearly'
        : billingPeriod === 'monthly' && (customPeriodLength || 0) > 1
        ? `per ${customPeriodLength} months`
        : 'monthly';

    const modalText =
      selectedLevel.level > sendAllotmentLevel
        ? `You've successfully added additional credits to your subscription. 
        The total sends allotment is ${formattedName} credits ${period}. 
        You'll be charged ${period} $${price} extra.`
        : `You've successfully downgraded to a lower send tier. You will be charged a lower amount on your next bill`;

    openBillingModal({
      level: selectedLevel,
      subscription,
      facebookPage: page,
      plan: planTab.plan!,
      onSuccessBilling: () =>
        openGeneralTextModal({
          header: 'Credits changed',
          message: modalText,
        }),
    });
    setSelectedLevelChanged(false);
  };

  const customOptionRenderer = (option: DropdownOption) => {
    const { price, formattedName } = getFormatedPriceAndName(option);
    return (
      <span>
        {subscription && (
          <span className="billing-page-card__select--number">${price} -</span>
        )}
        {subscription && ' '}
        <span className="billing-page-card__select">
          {formattedName} credits/{billingPeriod}{' '}
        </span>
      </span>
    );
  };

  const showDropDownForCustomTeam = () => {
    if (isCustomTeam) {
      if (isNil(subscription?.maxSendAllotment)) {
        return true;
      }
      if (subscription?.maxSendAllotment === 0) {
        return isEmpty(subscription.smsNumbers);
      }
      return (subscription?.maxSendAllotment || 0) > 0 ? false : true;
    }
    return isLegacyTeamPlan && isEmpty(subscription?.smsNumbers);
  };

  if (
    isFreePlan ||
    !plansAllotmentsOptions.length ||
    !isOneOfSupportedPlans ||
    !accountDetails?.subscriptions?.length ||
    !subscription?.endsAt ||
    subscriptionEndsAt !== 'n/a' ||
    showDropDownForCustomTeam()
  ) {
    return null;
  }

  return (
    <div>
      <div className="active-plan-details__select-wrapper">
        <PrimarySelect
          showSearchFilter={false}
          className="active-plan-details__select-card"
          placeholder="Choose a plan"
          onChange={(plan: { id: number; name: string; level: any }) =>
            onDropdownItemChange(plan)
          }
          disabled={showPopup}
          options={plansAllotmentsOptions}
          value={selectedLevel}
          customOptionRenderer={customOptionRenderer}
        />
        {showPopup && (
          <PrimaryToolTip
            useHover={true}
            classNameContent="active-plan-details__tooltip-padding"
            classNameTitle="active-plan-details__tooltip-title"
            align="end"
            withoutLine
            title="Credits Downgraded"
            anchorElement={<i className="far fa-question-circle" />}
          >
            Lowered since next billing cycle to{' '}
            {abbreviateNumber(loweredSendsAmount || 0)} credits
          </PrimaryToolTip>
        )}
        {selectedLevelChanged && (
          <PrimaryButton
            onClick={onOpenConfirmSendsModal}
            className="active-plan-details__action"
          >
            Change Limits
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};
